import { doc, writeBatch } from 'firebase/firestore';
import { db } from '../../../firebaseConfig';
import { isKingRole } from '../utils/gameUtils';
import { batchPushChat } from '../utils/chatStore';

// Manages game actions: draw offers, resign, revert move
export const useGameActions = (gameId, game, chess, currentUser, isAdmin, playerData, playerTeam, setChessPosition) => {
  const hasKingRoleCheck = () => isKingRole(playerData?.role);

  // Offer draw (King only)
  const offerDraw = async () => {
    if (!hasKingRoleCheck() || !playerTeam || !game) return;

    try {
      const gameRef = doc(db, 'games', gameId);

      const offerMessage = {
        sender: 'SYSTEM',
        message: `${playerTeam === 'white' ? 'White' : 'Black'} King offers a draw!`,
        timestamp: new Date().toISOString(),
        isDrawOffer: true
      };

      const batch = writeBatch(db);
      batch.update(gameRef, {
        drawOffer: {
          from: playerTeam,
          timestamp: new Date().toISOString()
        }
      });
      batchPushChat(batch, gameId, { ...offerMessage, team: 'white' });
      batchPushChat(batch, gameId, { ...offerMessage, team: 'black' });
      await batch.commit();
    } catch (error) {
      console.error('Error offering draw:', error);
    }
  };

  // Accept draw offer (King only)
  const acceptDraw = async () => {
    if (!hasKingRoleCheck() || !playerTeam || !game?.drawOffer) return;
    if (game.drawOffer.from === playerTeam) return;

    try {
      const gameRef = doc(db, 'games', gameId);

      const acceptMessage = {
        sender: 'SYSTEM',
        message: `Draw accepted! Game ends in a draw.`,
        timestamp: new Date().toISOString(),
        isDrawAccept: true
      };

      const batch = writeBatch(db);
      batch.update(gameRef, {
        status: 'completed',
        completedAt: new Date().toISOString(),
        winner: null,
        endReason: 'Draw by agreement',
        drawOffer: null
      });
      batchPushChat(batch, gameId, { ...acceptMessage, team: 'white' });
      batchPushChat(batch, gameId, { ...acceptMessage, team: 'black' });
      await batch.commit();
    } catch (error) {
      console.error('Error accepting draw:', error);
    }
  };

  // Decline draw offer (King only)
  const declineDraw = async () => {
    if (!hasKingRoleCheck() || !playerTeam || !game?.drawOffer) return;
    if (game.drawOffer.from === playerTeam) return;

    try {
      const gameRef = doc(db, 'games', gameId);

      const declineMessage = {
        sender: 'SYSTEM',
        message: `${playerTeam === 'white' ? 'White' : 'Black'} King declined the draw offer.`,
        timestamp: new Date().toISOString(),
        isDrawDecline: true
      };

      const batch = writeBatch(db);
      batch.update(gameRef, { drawOffer: null });
      batchPushChat(batch, gameId, { ...declineMessage, team: 'white' });
      batchPushChat(batch, gameId, { ...declineMessage, team: 'black' });
      await batch.commit();
    } catch (error) {
      console.error('Error declining draw:', error);
    }
  };

  // Resign (King only)
  const resign = async () => {
    if (!hasKingRoleCheck() || !playerTeam || !game) return;

    try {
      const gameRef = doc(db, 'games', gameId);
      const opponentTeam = playerTeam === 'white' ? 'black' : 'white';

      const resignMessage = {
        sender: 'SYSTEM',
        message: `${playerTeam === 'white' ? 'White' : 'Black'} King resigned! ${opponentTeam === 'white' ? 'White' : 'Black'} wins!`,
        timestamp: new Date().toISOString(),
        isResign: true
      };

      const batch = writeBatch(db);
      batch.update(gameRef, {
        status: 'completed',
        completedAt: new Date().toISOString(),
        winner: opponentTeam,
        endReason: `${playerTeam === 'white' ? 'White' : 'Black'} resigned`,
        drawOffer: null
      });
      batchPushChat(batch, gameId, { ...resignMessage, team: 'white' });
      batchPushChat(batch, gameId, { ...resignMessage, team: 'black' });
      await batch.commit();
    } catch (error) {
      console.error('Error resigning:', error);
    }
  };

  // Admin revert last move
  const revertLastMove = async () => {
    if (!isAdmin || !game) return;

    const moveHistory = game.moveHistory || [];
    if (moveHistory.length === 0) return;

    const lastMove = moveHistory[moveHistory.length - 1];
    if (!lastMove.previousFen) return;

    try {
      const gameRef = doc(db, 'games', gameId);
      const newMoveHistory = moveHistory.slice(0, -1);

      const updateData = {
        fen: lastMove.previousFen,
        currentTurn: lastMove.previousTurn,
        moveHistory: newMoveHistory,
        eliminatedPlayers: lastMove.previousEliminatedPlayers || [],
        'suggestedMoves.white': [],
        'suggestedMoves.black': [],
        'activePolls.white': null,
        'activePolls.black': null,
        'turnTimer.white': null,
        'turnTimer.black': null
      };

      if (lastMove.previousPieces) {
        updateData.pieces = lastMove.previousPieces;
      }

      if (lastMove.previousWhiteTeam) {
        updateData.whiteTeam = lastMove.previousWhiteTeam;
      }
      if (lastMove.previousBlackTeam) {
        updateData.blackTeam = lastMove.previousBlackTeam;
      }

      const revertMessage = {
        sender: 'SYSTEM',
        message: `Admin reverted the last move (${lastMove.notation}). Game state restored.`,
        timestamp: new Date().toISOString(),
        isRevert: true
      };

      const batch = writeBatch(db);
      batch.update(gameRef, updateData);
      batchPushChat(batch, gameId, { ...revertMessage, team: 'white' });
      batchPushChat(batch, gameId, { ...revertMessage, team: 'black' });
      await batch.commit();

      chess.load(lastMove.previousFen);
      setChessPosition(lastMove.previousFen);
    } catch (error) {
      console.error('Error reverting move:', error);
    }
  };

  // Check draw offer status
  const hasPendingDrawOffer = () => {
    if (!game?.drawOffer || !playerTeam) return false;
    return game.drawOffer.from !== playerTeam;
  };

  const madeDrawOffer = () => {
    if (!game?.drawOffer || !playerTeam) return false;
    return game.drawOffer.from === playerTeam;
  };

  return {
    offerDraw,
    acceptDraw,
    declineDraw,
    resign,
    revertLastMove,
    hasPendingDrawOffer,
    madeDrawOffer,
  };
};
